import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { client } from "../../..";
import { WA_SAVE_QUICK_REPLY, WA_DELETE_QUICK_REPLY } from "../../../Queries/WhatsApp";
import { upsertWaQuickReply, removeWaQuickReply } from "../../../Slices/whatsappSlice";
import SidePanel from "./SidePanel";
import { gqlErrorMessage } from "./waUtils";

const EMPTY = { id: null, title: "", shortcut: "", body: "", category: "General", scope: "personal" };

/**
 * Browse / insert / manage saved replies. Team replies are managed by WhatsApp
 * admins; everyone can keep personal ones. Placeholders: {first_name}, {name}, {my_name}.
 */
const QuickRepliesPanel = ({ onClose, onInsert }) => {
  const dispatch = useDispatch();
  const replies = useSelector((s) => s.whatsapp.quickReplies);
  const { canManage, userId } = useSelector((s) => s.whatsapp.status || {});
  const [filter, setFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const grouped = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const list = replies.filter((r) => !q || `${r.title} ${r.shortcut} ${r.body} ${r.category}`.toLowerCase().includes(q));
    return list.reduce((acc, r) => {
      (acc[r.category || "General"] = acc[r.category || "General"] || []).push(r);
      return acc;
    }, {});
  }, [replies, filter]);

  const canEdit = (r) => canManage || (r.scope === "personal" && r.ownerId === userId);

  const save = async () => {
    if (!editing.title.trim() || !editing.body.trim() || saving) return;
    setSaving(true);
    setError(null);
    try {
      const { data } = await client.mutate({ mutation: WA_SAVE_QUICK_REPLY, variables: { ...editing } });
      dispatch(upsertWaQuickReply(data.whatsappSaveQuickReply));
      setEditing(null);
    } catch (err) {
      setError(gqlErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  // Two-step inline confirm (no pop-up dialogs in the inbox).
  const remove = async (r) => {
    if (confirmDeleteId !== r.id) {
      setConfirmDeleteId(r.id);
      return;
    }
    setConfirmDeleteId(null);
    try {
      await client.mutate({ mutation: WA_DELETE_QUICK_REPLY, variables: { id: r.id } });
      dispatch(removeWaQuickReply(r.id));
    } catch (err) {
      setError(gqlErrorMessage(err));
    }
  };

  if (editing) {
    return (
      <SidePanel
        title={editing.id ? "Edit quick reply" : "New quick reply"}
        subtitle="Use {first_name}, {name} or {my_name} — they're filled in when inserted."
        onClose={() => setEditing(null)}
        footer={
          <>
            {error && <span className="wa-help" style={{ color: "var(--wa-danger)" }}>{error}</span>}
            <button type="button" className="wa-btn" onClick={() => setEditing(null)}>Back</button>
            <button type="button" className="wa-btn wa-btn--primary" disabled={!editing.title.trim() || !editing.body.trim() || saving} onClick={save}>
              {saving ? <i className="fa-solid fa-spinner wa-spin" /> : <i className="fa-solid fa-check" />} Save
            </button>
          </>
        }
      >
        <div className="wa-field">
          <label className="wa-label" htmlFor="wa-qr-title">Title</label>
          <input id="wa-qr-title" className="wa-input" value={editing.title} maxLength={80} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="Pricing overview" />
        </div>
        <div className="wa-field" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div>
            <label className="wa-label" htmlFor="wa-qr-shortcut">Shortcut</label>
            <input id="wa-qr-shortcut" className="wa-input" value={editing.shortcut} maxLength={30} onChange={(e) => setEditing({ ...editing, shortcut: e.target.value })} placeholder="pricing" />
            <div className="wa-help">Type /{editing.shortcut || "shortcut"} in the composer</div>
          </div>
          <div>
            <label className="wa-label" htmlFor="wa-qr-category">Category</label>
            <input id="wa-qr-category" className="wa-input" value={editing.category} maxLength={40} onChange={(e) => setEditing({ ...editing, category: e.target.value })} placeholder="Sales" />
          </div>
        </div>
        <div className="wa-field">
          <label className="wa-label" htmlFor="wa-qr-body">Message</label>
          <textarea id="wa-qr-body" className="wa-textarea" rows={7} value={editing.body} maxLength={4096} onChange={(e) => setEditing({ ...editing, body: e.target.value })} placeholder="Hi {first_name}, …" />
        </div>
        <div className="wa-field">
          <label className="wa-label">Visible to</label>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" className={`wa-btn wa-btn--sm ${editing.scope === "personal" ? "wa-btn--primary" : ""}`} onClick={() => setEditing({ ...editing, scope: "personal" })}>Only me</button>
            <button type="button" className={`wa-btn wa-btn--sm ${editing.scope === "team" ? "wa-btn--primary" : ""}`} disabled={!canManage} onClick={() => setEditing({ ...editing, scope: "team" })} title={canManage ? "" : "Only WhatsApp admins can create team replies"}>Whole team</button>
          </div>
        </div>
      </SidePanel>
    );
  }

  return (
    <SidePanel
      title="Quick replies"
      subtitle="Saved answers for common questions. Click Insert, or type / in the composer."
      onClose={onClose}
      footer={
        <>
          {error && <span className="wa-help" style={{ color: "var(--wa-danger)" }}>{error}</span>}
          <button type="button" className="wa-btn wa-btn--primary" onClick={() => setEditing({ ...EMPTY })}><i className="fa-solid fa-plus" /> New quick reply</button>
        </>
      }
    >
      <div className="wa-search" style={{ marginBottom: 12 }}>
        <i className="fa-solid fa-magnifying-glass" />
        <input className="wa-input" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter quick replies" autoFocus />
      </div>
      {!replies.length && (
        <div className="wa-empty" style={{ height: "auto" }}>
          <i className="fa-solid fa-bolt wa-empty__icon" />
          <div className="wa-empty__title">No quick replies yet</div>
          <div>Save answers you send often — pricing, onboarding steps, meeting links.</div>
        </div>
      )}
      {Object.entries(grouped).map(([category, list]) => (
        <div key={category} style={{ marginBottom: 14 }}>
          <div className="wa-section__title">{category}</div>
          {list.map((r) => (
            <div className="wa-record" key={r.id}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <b style={{ flex: 1 }}>{r.title}</b>
                {r.shortcut && <span className="wa-chip wa-chip--accent">/{r.shortcut}</span>}
                <span className="wa-chip">{r.scope === "team" ? "Team" : "Personal"}</span>
              </div>
              <div className="wa-help" style={{ whiteSpace: "pre-wrap", maxHeight: 80, overflow: "hidden" }}>{r.body}</div>
              <div className="wa-record__actions">
                {onInsert && <button type="button" className="wa-btn wa-btn--sm wa-btn--primary" onClick={() => onInsert(r)}>Insert</button>}
                {canEdit(r) && <button type="button" className="wa-btn wa-btn--sm" onClick={() => setEditing({ id: r.id, title: r.title, shortcut: r.shortcut || "", body: r.body, category: r.category || "General", scope: r.scope })}>Edit</button>}
                {canEdit(r) && (
                  <button type="button" className="wa-btn wa-btn--sm wa-btn--danger" onClick={() => remove(r)} onBlur={() => setConfirmDeleteId(null)}>
                    {confirmDeleteId === r.id ? "Click again to delete" : "Delete"}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      ))}
    </SidePanel>
  );
};

export default QuickRepliesPanel;
